import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { useCloseOnEscape } from "hooks/useCloseOnEscape";
import { useTranslation } from "react-i18next";
import type {
  GroupByOption,
  IntervalOption,
  SizeOption,
} from "../hooks/useScheduleToolbarFilters";
import {
  HeaderRow,
  HiddenRadio,
  Overlay,
  Panel,
  RadioControl,
  RadioGroup,
  RadioLabel,
  RadioOption,
  Section,
  SectionDescription,
  SectionTitle,
  Title,
} from "./scheduleOptionsPanel.styled";

interface ScheduleOptionsPanelProps {
  isOpen: boolean;
  intervalOption: IntervalOption;
  groupByOption: GroupByOption;
  sizeOption: SizeOption;
  onGroupByChange: (value: GroupByOption) => void;
  onSizeChange: (value: SizeOption) => void;
  onClose: () => void;
}

export const ScheduleOptionsPanel = (
  props: ScheduleOptionsPanelProps,
): JSX.Element | null => {
  const { t } = useTranslation();

  useCloseOnEscape({
    isOpen: props.isOpen,
    onClose: props.onClose,
    stopPropagation: true,
  });

  if (!props.isOpen) return null;

  return (
    <Overlay onClick={props.onClose}>
      <Panel onClick={(event) => event.stopPropagation()}>
        <HeaderRow>
          <Title>{t("Schedule.Options")}</Title>
          <SecondaryButton
            iconName="v2/cross"
            size="small"
            aria-label={t("DesignComponents.CloseModal")}
            onClick={props.onClose}
          />
        </HeaderRow>

        <Section>
          <SectionTitle>{t("Schedule.GroupBy")}</SectionTitle>
          <SectionDescription>{t("Schedule.GroupByDescription")}</SectionDescription>
          <RadioGroup role="radiogroup" aria-label={t("Schedule.GroupBy")}>
            <RadioOption>
              <HiddenRadio
                type="radio"
                name="schedule-group-by"
                checked={props.groupByOption === "team"}
                onChange={() => props.onGroupByChange("team")}
              />
              <RadioControl />
              <RadioLabel>{t("Schedule.Team")}</RadioLabel>
            </RadioOption>
            <RadioOption>
              <HiddenRadio
                type="radio"
                name="schedule-group-by"
                checked={props.groupByOption === "person"}
                onChange={() => props.onGroupByChange("person")}
              />
              <RadioControl />
              <RadioLabel>{t("Schedule.Person")}</RadioLabel>
            </RadioOption>
          </RadioGroup>
        </Section>

        {props.intervalOption === "weekly" && (
          <Section>
            <SectionTitle>{t("Schedule.ScheduleSize")}</SectionTitle>
            <SectionDescription>
              {t("Schedule.ScheduleSizeDescription")}
            </SectionDescription>
            <RadioGroup role="radiogroup" aria-label={t("Schedule.ScheduleSize")}>
              <RadioOption>
                <HiddenRadio
                  type="radio"
                  name="schedule-size"
                  checked={props.sizeOption === "large"}
                  onChange={() => props.onSizeChange("large")}
                />
                <RadioControl />
                <RadioLabel>{t("Schedule.Large")}</RadioLabel>
              </RadioOption>
              <RadioOption>
                <HiddenRadio
                  type="radio"
                  name="schedule-size"
                  checked={props.sizeOption === "small"}
                  onChange={() => props.onSizeChange("small")}
                />
                <RadioControl />
                <RadioLabel>{t("Schedule.Small")}</RadioLabel>
              </RadioOption>
            </RadioGroup>
          </Section>
        )}
      </Panel>
    </Overlay>
  );
};
